import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { type ReferenceId } from '@canvas/comments-types';

import { DelegatingApolloClient } from '../delegating-apollo-client';
import { type DelegatingClient } from '../delegating-client-types';
import { DelegatingGraphQLClient } from '../delegating-graphql-client';
import { throwOnMutationError, throwOnQueryError } from '../graphql-response';

import {
  type ListCommentQuery,
  type CreateCommentMutation,
  type CreateCommentMutationInput,
  type ListCommentQueryInput,
  type ConfluenceComment,
  type UpdateCommentMutation,
  type UpdateCommentMutationInput,
  type DeleteCommentMutation,
  type DeleteCommentMutationInput,
  type ResolveCommentMutation,
  type ResolveCommentMutationInput,
  type ConfluenceSaveReactionResponse,
  type ConfluenceReactionsSummaryResponse,
  type ConfluenceReactedUsersResponse,
  type CreateOrDeleteCommentReactionInput,
  type DeleteCommentReactionMutation,
  type AddCommentReactionMutation,
  type ListCommentReactionInput,
  type ListCommentReactionQuery,
  type GetCommentReactionDetailsInput,
  type GetCommentReactionDetailsQuery,
  type ContentTypesHeaderQuery,
  type ContentTypesHeaderQueryInput,
  type ListCommentResponse,
  type CreateMentionNotificationMutationInput,
  type CreateMentionNotificationMutation,
  type ContentPermissionsInput,
  type ContentPermissionsQueryResponse,
  type ContentPermissionsResponse,
  type ContentPermissionsUser,
  type ConfluenceViewedCommentsResponse,
  type ViewedCommentsInput,
  type ViewedCommentsQuery,
  type MarkCommentAsViewedMutation,
  type MarkCommentAsViewedInput,
  type LabelSearchQuery,
  type LabelSearchQueryInput,
  type UpdateContentTitleMutation,
  type UpdateContentTitleInput,
  type ConfluenceContent,
  type CreateContentMutationInput,
  type CreateContentMutation,
  type CommentDetailsQueryInput,
  type CommentDetailsQuery,
} from './confluence-client-types';
import {
  type CanvasSessionBulkTokensArrayResponse,
  type ConfluenceRestClientOptions,
  ConfluenceRestClient,
} from './confluence-rest-client';
import * as ConfluenceAddCommentReactionMutation from './queries/confluence-mutation-add-comment-reaction.graphql';
import * as ConfluenceCreateCommentMutation from './queries/confluence-mutation-create-comment.graphql';
import * as ConfluenceCreateContentMutation from './queries/confluence-mutation-create-content.graphql';
import * as ConfluenceCreateMentionNotificationMutation from './queries/confluence-mutation-create-mention-notification.graphql';
import * as ConfluenceDeleteCommentReactionMutation from './queries/confluence-mutation-delete-comment-reaction.graphql';
import * as ConfluenceDeleteCommentMutation from './queries/confluence-mutation-delete-comment.graphql';
import * as ConfluenceMarkCommentAsViewedMutation from './queries/confluence-mutation-mark-comment-as-read.graphql';
import * as ConfluenceResolveCommentMutation from './queries/confluence-mutation-resolve-comment.graphql';
import * as ConfluenceUpdateCommentMutation from './queries/confluence-mutation-update-comment.graphql';
import * as ConfluenceUpdateContentTitleMutation from './queries/confluence-mutation-update-content-title.graphql';
import * as ConfluenceContentTypesHeaderQuery from './queries/confluence-query-content-types-header.graphql';
import * as ConfluenceGetViewedCommentsQuery from './queries/confluence-query-content-viewed-comments.graphql';
import * as ConfluenceGetCommentCountQuery from './queries/confluence-query-get-comment-count.graphql';
import * as ConfluenceCommentDetailsQuery from './queries/confluence-query-get-comment-details.graphql';
import * as ConfluenceGetCommentReactionDetailsQuery from './queries/confluence-query-get-comment-reaction-details.graphql';
import * as ConfluenceContentPermissionsQuery from './queries/confluence-query-get-content-permissions.graphql';
import * as ConfluenceLabelSearchQuery from './queries/confluence-query-label-search.graphql';
import * as ConfluenceListCommentReactionQuery from './queries/confluence-query-list-comment-reactions.graphql';
import * as ConfluenceListCommentQuery from './queries/confluence-query-list-comments.graphql';

/**
 * The whiteboard (or containing content) in which the comment will live.
 */
type ContainerId = string;

/**
 * A reference to a top-level comment.
 */
type CommentId = string;
/**
 * An optional reference to a parent which is a comment.
 */
type ParentCommentId = string;

/**
 * The ADF which represents the comment's contents.
 */
type AdfContent = string;

type NotificationAction = 'NOTIFY' | 'DONT_NOTIFY';

interface AddCommentOpts {
  baseUrl: string;
  containerId: ContainerId;
  parentCommentId?: ParentCommentId;
  content: AdfContent;
  createdFrom?: 'WHITEBOARD' | 'DATABASE';
  originalSelection?: string;
}

interface UpdateCommentOpts {
  baseUrl: string;
  commentId: CommentId;
  parentCommentId?: string;
  content: AdfContent;
}

interface DeleteCommentOpts {
  baseUrl: string;
  commentId: CommentId;
}

interface ResolveCommentOpts {
  baseUrl: string;
  commentId: CommentId;
  resolved: boolean;
}

export interface CommentDetailsOpts {
  containerId: string;
  commentId: string;
}

interface ListCommentOpts {
  baseUrl: string;
  containerId: ContainerId;
  parentCommentId?: ParentCommentId;
  last?: number;
  noCache?: boolean;
  before?: string;
}

interface CommentReactionOpts {
  baseUrl: string;
  containerId: ContainerId;
  commentId: CommentId;
  emojiId: string;
  containerType?: string;
}

interface ListCommentReactionOpts {
  baseUrl: string;
  comments: {
    containerId: ContainerId;
    commentId: CommentId;
  }[];
}

interface ViewedCommentsOpts {
  baseUrl: string;
  contentId: string;
  contentType: string;
}

interface MarkCommentsAsViewedOpts {
  baseUrl: string;
  commentIds: ReferenceId[];
}

interface BoardTitleOpts {
  baseUrl: string;
  contentId: string;
}

interface Mention {
  mentionLocalId: string;
  mentionedUserAccountId: string;
  notificationAction: NotificationAction;
}

interface MentionNotificationOpts {
  baseUrl: string;
  contentId: string;
  mentions: Mention[];
}

interface ContentPermissionsOpts {
  baseUrl: string;
  accountIds: string[];
  contentId: string;
}

interface LabelSearchOpts {
  baseUrl: string;
  contentId: string | undefined;
  searchText: string;
}

const isLegacyId = (id: string | undefined) => /^\d+$/.test(id ?? '');

type IdLocation = 'marker' | 'id';

export class ConfluenceClient {
  /**
   * Whiteboards aim to maintain loose coupling with external systems.
   * {@link referenceIdToContentIdMapping} maintains a mapping between a UUID
   * and externally owned ID for the purposes of loose coupling.
   *
   * @description Use this mapping via {@link toContentId} to go from UUID ->
   * external ID for API calls (most will still use external ID)
   * @description Use this mapping via {@link withReferenceId} to go from external
   * ID -> UUID for API calls (most will still use external ID)
   */
  private referenceIdToContentIdMapping = new Map<string, string>();
  private contentIdToReferenceIdMapping = new Map<string, string>();

  private readonly clients: DelegatingClient = this.createClients();

  constructor() {}

  private createClients(): DelegatingClient {
    if (isFeatureFlagEnabled('useNewGraphQLClient')) {
      return new DelegatingGraphQLClient();
    }
    return new DelegatingApolloClient();
  }

  private getClient(baseUrl: string) {
    return this.clients.getOrCreate(baseUrl, '/gateway/api/graphql/slow');
  }

  public async addComment({
    baseUrl,
    containerId,
    content,
    parentCommentId,
    createdFrom = 'WHITEBOARD',
    originalSelection = '',
  }: AddCommentOpts): Promise<ConfluenceComment> {
    const client = this.getClient(baseUrl);
    const contentId = parentCommentId ? this.toContentId(parentCommentId) : undefined;

    const responseOrErr = await client
      .mutate<CreateCommentMutation['data'], CreateCommentMutationInput>({
        mutation: ConfluenceCreateCommentMutation.CreateInlineComment,
        variables: {
          input: {
            containerId: containerId.toString(),
            parentCommentId: contentId,
            createdFrom: createdFrom,
            commentBody: {
              value: content,
              representationFormat: 'ATLAS_DOC_FORMAT',
            },
            lastFetchTimeMillis: Date.now(),
            matchIndex: 0,
            numMatches: 1,
            originalSelection,
          },
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    const idLocation: IdLocation = parentCommentId ? 'id' : 'marker';
    return this.withReferenceId(responseOrErr.data.createInlineComment, idLocation);
  }

  public async updateComment({
    baseUrl,
    parentCommentId,
    commentId,
    content,
  }: UpdateCommentOpts): Promise<ConfluenceComment> {
    const client = this.getClient(baseUrl);
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<UpdateCommentMutation['data'], UpdateCommentMutationInput>({
        mutation: ConfluenceUpdateCommentMutation.UpdateInlineComment,
        variables: {
          input: {
            commentId: contentId,
            commentBody: {
              value: content,
              representationFormat: 'ATLAS_DOC_FORMAT',
            },
          },
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    const idLocation: IdLocation = parentCommentId ? 'id' : 'marker';
    return this.withReferenceId(responseOrErr.data.updateComment, idLocation);
  }

  public async deleteComment({ baseUrl, commentId }: DeleteCommentOpts): Promise<void> {
    const client = this.getClient(baseUrl);
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<DeleteCommentMutation['data'], DeleteCommentMutationInput>({
        mutation: ConfluenceDeleteCommentMutation.DeleteInlineComment,
        variables: {
          commentIdToDelete: contentId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    if (responseOrErr.data.deleteComment === false) {
      throw new Error(`failed deletion: ${JSON.stringify(responseOrErr.errors)}`);
    }

    return;
  }

  public async resolveComment({
    baseUrl,
    commentId,
    resolved,
  }: ResolveCommentOpts): Promise<void> {
    const client = this.getClient(baseUrl);
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<ResolveCommentMutation['data'], ResolveCommentMutationInput>({
        mutation: ConfluenceResolveCommentMutation.ResolveInlineComment,
        variables: { commentId: contentId, resolved },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    if (responseOrErr.data.resolveInlineComment.status === false) {
      throw new Error(`failed resolve: ${JSON.stringify(responseOrErr.errors)}`);
    }

    return;
  }

  /**
   * Fetches the details of a specific comment within a content item.
   *
   * @returns The comment details if found, otherwise `undefined`.
   */
  public async getCommentDetails(
    baseUrl: string,
    { containerId, commentId }: CommentDetailsOpts
  ): Promise<ConfluenceComment | undefined> {
    const client = this.getClient(baseUrl);

    const variables: CommentDetailsQueryInput = {
      contentId: containerId.toString(),
    };

    if (isLegacyId(commentId)) {
      variables.commentId = commentId;
    } else if (commentId) {
      variables.inlineMarkerRef = commentId;
    }

    const responseOrErr = await client
      .query<CommentDetailsQuery, CommentDetailsQueryInput>({
        query: ConfluenceCommentDetailsQuery.CommentDetails,
        variables,
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    if (responseOrErr.data.comments.edges.length === 0) {
      return undefined;
    }

    return this.withReferenceId(responseOrErr.data.comments.edges[0].node);
  }

  public async listComments({
    baseUrl,
    containerId,
    parentCommentId,
    last,
    noCache = false,
    before,
  }: ListCommentOpts): Promise<ListCommentResponse> {
    const client = this.getClient(baseUrl);

    const variables: ListCommentQueryInput = {
      contentId: containerId.toString(),
      last: last ?? 50,
      offset: before ?? undefined,
    };

    if (isLegacyId(parentCommentId)) {
      variables.commentId = parentCommentId;
    } else if (parentCommentId) {
      variables.inlineMarkerRef = parentCommentId;
    }

    const responseOrErr = await client
      .query<ListCommentQuery, ListCommentQueryInput>({
        query: ConfluenceListCommentQuery.CommentList,
        variables,
        context: { tenantBaseUrl: baseUrl.toString() },
        fetchPolicy: noCache ? 'no-cache' : undefined,
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return {
      comments: responseOrErr.data.comments.edges.map((e) =>
        this.withReferenceId(e.node)
      ),
      totalCount: responseOrErr.data.comments.totalCount,
      cursor: responseOrErr.data.comments.edges[0].cursor,
    };
  }

  public async getCommentCount({
    baseUrl,
    containerId,
    last,
    noCache = false,
  }: ListCommentOpts): Promise<number> {
    const client = this.getClient(baseUrl);

    const variables: ListCommentQueryInput = {
      contentId: containerId.toString(),
      last: last ?? 50,
    };

    const responseOrErr = await client
      .query<ListCommentQuery, ListCommentQueryInput>({
        query: ConfluenceGetCommentCountQuery.CommentCount,
        variables,
        context: { tenantBaseUrl: baseUrl.toString() },
        fetchPolicy: noCache ? 'no-cache' : undefined,
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.comments.totalCount;
  }

  public async confluence_addCommentReaction({
    baseUrl,
    containerId,
    commentId,
    emojiId,
    containerType = 'WHITEBOARD',
    cloudId,
  }: CommentReactionOpts & { cloudId: string }): Promise<ConfluenceSaveReactionResponse> {
    const client = this.getClient(baseUrl);
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<
        AddCommentReactionMutation['data'],
        { cloudId: string; input: CreateOrDeleteCommentReactionInput }
      >({
        mutation: ConfluenceAddCommentReactionMutation.AddCommentReaction,
        variables: {
          cloudId,
          input: {
            contentId,
            contentType: 'COMMENT',
            containerId,
            containerType: containerType,
            emojiId,
          },
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    return responseOrErr.data.confluence_addReaction;
  }

  public async deleteCommentReaction({
    baseUrl,
    containerId,
    commentId,
    emojiId,
    containerType = 'WHITEBOARD',
  }: CommentReactionOpts): Promise<ConfluenceSaveReactionResponse> {
    const client = this.getClient(baseUrl);
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .mutate<DeleteCommentReactionMutation['data'], CreateOrDeleteCommentReactionInput>({
        mutation: ConfluenceDeleteCommentReactionMutation.DeleteCommentReaction,
        variables: {
          contentId,
          contentType: 'COMMENT',
          containerId,
          containerType: containerType,
          emojiId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnMutationError(responseOrErr);

    return responseOrErr.data.deleteReaction;
  }

  public async listCommentReactions({
    baseUrl,
    comments,
  }: ListCommentReactionOpts): Promise<ConfluenceReactionsSummaryResponse[]> {
    const client = this.getClient(baseUrl);
    const responseOrErr = await client
      .query<ListCommentReactionQuery['data'], ListCommentReactionInput>({
        query: ConfluenceListCommentReactionQuery.CommentReactionList,
        variables: {
          comments: comments.map(({ containerId, commentId }) => {
            const contentId = this.toContentId(commentId);

            return {
              contentId,
              contentType: 'COMMENT',
              containerId,
              containerType: 'WHITEBOARD',
            };
          }),
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.reactionsSummaryList;
  }

  public async getViewedComments({
    baseUrl,
    contentId,
    contentType,
  }: ViewedCommentsOpts): Promise<ConfluenceViewedCommentsResponse> {
    const client = this.getClient(baseUrl);
    const responseOrErr = await client
      .query<ViewedCommentsQuery['data'], ViewedCommentsInput>({
        query: ConfluenceGetViewedCommentsQuery.contentAnalyticsViewedComments,
        variables: { contentId, contentType },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.contentAnalyticsViewedComments;
  }

  public async markCommentsAsViewed({
    baseUrl,
    commentIds,
  }: MarkCommentsAsViewedOpts): Promise<void> {
    const client = this.getClient(baseUrl);
    await client
      .mutate<MarkCommentAsViewedMutation, MarkCommentAsViewedInput>({
        mutation: ConfluenceMarkCommentAsViewedMutation.markCommentsAsRead,
        variables: {
          input: { commentIds: commentIds.map((id) => this.toContentId(id)) },
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);
  }

  public async getCommentReactionDetails({
    baseUrl,
    containerId,
    commentId,
    emojiId,
  }: CommentReactionOpts): Promise<ConfluenceReactedUsersResponse> {
    const client = this.getClient(baseUrl);
    const contentId = this.toContentId(commentId);

    const responseOrErr = await client
      .query<GetCommentReactionDetailsQuery['data'], GetCommentReactionDetailsInput>({
        query: ConfluenceGetCommentReactionDetailsQuery.CommentReactionDetails,
        variables: {
          contentId,
          contentType: 'COMMENT',
          containerId,
          containerType: 'WHITEBOARD',
          emojiId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => err);

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.reactedUsers;
  }

  public async getWhiteboardBoardTitle(args: BoardTitleOpts): Promise<string> {
    return (await this.contentTypesHeaderQuery(args)).title;
  }

  public async getWhiteboardBoardTitleIsDefault(args: BoardTitleOpts): Promise<boolean> {
    const hasBeenTitled =
      (await this.contentTypesHeaderQuery(args))?.contentProperties?.latest
        ?.hasBeenTitled ?? null;

    return hasBeenTitled === false;
  }

  private async contentTypesHeaderQuery({ baseUrl, contentId }: BoardTitleOpts) {
    const client = this.getClient(baseUrl);
    const responseOrErr = await client
      .query<ContentTypesHeaderQuery['data'], ContentTypesHeaderQueryInput>({
        query: ConfluenceContentTypesHeaderQuery.ContentTypesHeaderQuery,
        variables: {
          contentId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        console.log(err);
        return err;
      });

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.content.nodes[0];
  }

  public async createMentionNotification({
    baseUrl,
    contentId,
    mentions,
  }: MentionNotificationOpts): Promise<void> {
    const client = this.getClient(baseUrl);
    const responseOrErr = await client
      .mutate<CreateMentionNotificationMutation, CreateMentionNotificationMutationInput>({
        mutation: ConfluenceCreateMentionNotificationMutation.CreateMentionNotification,
        variables: {
          contentId,
          mentions,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        return err;
      });

    throwOnMutationError(responseOrErr);

    return;
  }

  public async getContentLabels({
    baseUrl,
    contentId,
    searchText,
  }: LabelSearchOpts): Promise<{
    suggestedLabels: { name: string }[];
    otherLabels: { name: string }[];
  }> {
    const client = this.clients.getOrCreate(baseUrl, '/gateway/api/graphql');
    const responseOrErr = await client
      .query<LabelSearchQuery['data'], LabelSearchQueryInput>({
        query: ConfluenceLabelSearchQuery.LabelSearch,
        variables: {
          contentId,
          searchText,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        return err;
      });

    throwOnQueryError(responseOrErr);

    return responseOrErr.data.labelSearch;
  }

  public async getContentPermissions({
    baseUrl,
    accountIds,
    contentId,
  }: ContentPermissionsOpts): Promise<ContentPermissionsResponse> {
    const client = this.getClient(baseUrl);
    const responseOrErr = await client
      .query<ContentPermissionsQueryResponse, ContentPermissionsInput>({
        query: ConfluenceContentPermissionsQuery.ContentPermissionsQuery,
        variables: {
          accountIds,
          contentId,
        },
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        return err;
      });

    throwOnQueryError(responseOrErr);

    const users = new Map<string, ContentPermissionsUser>();
    const contentPermissionsData = responseOrErr.data;
    // `contentPermissions` specifies restrictions on the current item
    // `usersWithContentRestrictions` specifies restrictions on the current item's parent, if one exists
    const { contentPermissions, usersWithContentRestrictions } = contentPermissionsData;

    if (contentPermissions) {
      const usersWithAccess =
        contentPermissions.subjectsWithEffectiveContentPermissions?.edges;

      for (const userWithContentRestrictions of usersWithContentRestrictions) {
        const { accountId, profilePicture, hasSpaceViewPermission, restrictingContent } =
          userWithContentRestrictions;
        let userHasAccessToContent = false;
        let isParentDatabaseRestricted = false;
        let isSpaceRestricted = false;

        if (!hasSpaceViewPermission) {
          isSpaceRestricted = true;
        } else if (restrictingContent?.hasRestrictions) {
          // Parent item has content restrictions, which is inherited by this child item
          isParentDatabaseRestricted = true;
        } else {
          // Not a parent item, check permissions on this item
          userHasAccessToContent =
            usersWithAccess?.some(
              (user) => user.node.id === userWithContentRestrictions.accountId
            ) || contentPermissions.contentAccess !== 'EVERYONE_NO_ACCESS';
        }

        users.set(accountId, {
          accountId: accountId,
          profilePicture,
          userHasAccessToContent,
          isParentDatabaseRestricted,
          isSpaceRestricted,
        });
      }
    }

    return Array.from(users.values());
  }

  /**
   * Creates a new Confluence content item (e.g., page, whiteboard or database).
   */
  public async createContent(
    baseUrl: string,
    input: CreateContentMutationInput
  ): Promise<ConfluenceContent> {
    const client = this.getClient(baseUrl);
    const responseOrErr = await client
      .mutate<CreateContentMutation, CreateContentMutationInput>({
        mutation: ConfluenceCreateContentMutation.CreateContentMutation,
        variables: input,
        context: { tenantBaseUrl: baseUrl.toString() },
      })
      .catch((err): Error => {
        return err;
      });

    throwOnMutationError(responseOrErr);

    return responseOrErr.data.createContentContextual;
  }

  /**
   * Updates the title for a Confluence content item (e.g., whiteboard or database).
   */
  public async updateContentTitle(
    baseUrl: string,
    contentId: string,
    title: string,
    abortSignal?: AbortSignal
  ): Promise<void> {
    const client = this.getClient(baseUrl);
    await client.mutate<UpdateContentTitleMutation, UpdateContentTitleInput>({
      mutation: ConfluenceUpdateContentTitleMutation.updateTitleMutation,
      variables: {
        contentId,
        title,
      },
      context: {
        tenantBaseUrl: baseUrl.toString(),
        fetchOptions: { signal: abortSignal },
      },
    });
  }

  public async getCanvasSessionBulkTokens({
    baseUrl,
    contentIds,
  }: {
    baseUrl: ConfluenceRestClientOptions['baseUrl'];
    contentIds: string[];
  }): Promise<CanvasSessionBulkTokensArrayResponse> {
    const client = new ConfluenceRestClient({ baseUrl });

    return await client.getCanvasSessionBulkTokens(contentIds);
  }

  public toReferenceId(contentId: string) {
    return this.contentIdToReferenceIdMapping.get(contentId) ?? contentId;
  }

  public toContentId(referenceId: string) {
    return this.referenceIdToContentIdMapping.get(referenceId) ?? referenceId;
  }

  private withReferenceId(
    node: ConfluenceComment,
    idLocation: IdLocation = 'marker'
  ): ConfluenceComment {
    const contentId = node.id;
    const referenceId = idLocation === 'marker' ? node.location.inlineMarkerRef : node.id;

    this.referenceIdToContentIdMapping.set(referenceId, contentId);
    this.contentIdToReferenceIdMapping.set(contentId, node.location.inlineMarkerRef);

    return {
      ...node,
      id: referenceId,
      reactionsId: node.id,
      replies: node.replies?.map((r) => this.withReferenceId(r, 'id')),
    };
  }
}
